import React, { HTMLInputTypeAttribute, memo } from "react";
import TableRow, { DeleteButtonType, EditButtonType } from "./TableRow";
import TableTotals from "./TableTotals";

export type TableColumns<T> = {
  dataName?: keyof T;
  dataNameJSX?: keyof T;
  caption?: string;
  jsx?: React.JSX.Element;
  leftIcon?: React.JSX.Element;
  leftIconDataName?: keyof T;
  editable?: boolean;
  type?: HTMLInputTypeAttribute;
  total?: number | string;
  className?: string;
  align?: "Left" | "Center" | "Right";
};

type TableType<T> = {
  columns: TableColumns<T>[];
  data: T[];
  editButton?: EditButtonType<T>;
  deleteButton?: DeleteButtonType<T>;
  newRowFlag?: keyof T;
};

const Table = <T,>({
  columns,
  data,
  editButton,
  deleteButton,
  newRowFlag
}: TableType<T>) => {
  const dataRows: React.JSX.Element[] = [];

  const getAlignmentClass = (align?: "Left" | "Center" | "Right") => {
    switch (align) {
      case "Center":
        return "text-center";
      case "Right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  const columnsRow = columns.map((col, x) => (
    <th
      key={`heading - ${col?.dataName?.toString()} - ${x}`}
      className={[
        `Align${col.align ?? "Left"}`,
        "py-3 px-panel-padding pb-4 text-collins-md font-semibold text-muted-foreground",
        getAlignmentClass(col.align),
        x === 0 ? "" : "pl-0",
        col.className ?? ""
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {col.caption}
      {col.jsx}
    </th>
  ));

  data.forEach((row, y) => {
    dataRows[y] = (
      <TableRow
        key={`row - ${y}`}
        columns={columns}
        row={row}
        y={y}
        editButton={editButton}
        deleteButton={deleteButton}
        newRowFlag={newRowFlag}
        getAlignmentClass={getAlignmentClass}
      />
    );
  });

  return (
    <table className="CustomTable w-full border-collapse">
      <thead>
        <tr className="border-b-2 border-border">
          <th className="td-Padding w-panel-padding p-0" />
          {columnsRow}
          <th className="th-Padding w-panel-padding p-0" />
        </tr>
      </thead>
      <tbody>
        {dataRows}
        {data.length > 0 && (
          <TableTotals
            columns={columns}
            getAlignmentClass={getAlignmentClass}
          />
        )}
      </tbody>
    </table>
  );
};

export default memo(Table) as typeof Table;
